/**
 * ai-credits.ts — Phần THUẦN (không React) của tính năng "AI hai chế độ":
 * AI của eCDP (trừ credit) / Key của tôi (BYOK). Định dạng credit, suy ra trạng thái
 * ví, nhãn nguồn, lọc/khoá model, tạm tính + VAT gói mua credit, câu lỗi theo mã.
 * Hợp đồng API: docs/AI-2-CHE-DO-HOP-DONG-API.md.
 */
import { apiErrorText } from './api-error-text'
import { i18n, currentLocale } from '@/lib/i18n'

// ── Kiểu dữ liệu theo hợp đồng ───────────────────────────────────────

export type AiSource = 'platform' | 'byok'
export type WalletStatus = 'ok' | 'low' | 'empty' | 'blocked'
export type EmbeddingOverage = 'credit' | 'stop'

export interface ByokState {
  configured: boolean
  hint: string | null
  /** active | invalid | error … (BE trả chuỗi). */
  status: string | null
  lastError: string | null
  lastVerifiedAt: string | null
}

export interface AiProviderSource {
  provider: string
  name: string
  source: AiSource
  byok: ByokState
  platformAvailable: boolean
}

export interface AiSources {
  defaultSource: AiSource
  providers: AiProviderSource[]
  platformEnabled: boolean
  orgBlocked: boolean
}

export interface AiModelEntry {
  provider: string
  model: string
  title: string
  tier: string | null
  source: AiSource
  allowed: boolean
  lockReason: string | null
  /** Cũ (đơn vị nội bộ / lượt) — giao diện thôi dùng, giữ cho tương thích. null với Key của tôi. */
  creditsPerTurn: number | null
  priceIn: number | null
  priceOut: number | null
  /** Giá bán ra (đ / 1 triệu token) — chỉ AI của eCDP. Server cũ: undefined. */
  salePricePer1M?: { inVnd: number; outVnd: number } | null
  /** Số dư ví đủ ≈ bao nhiêu token của model này — chỉ AI của eCDP. Server cũ: undefined. */
  tokensForBalance?: number | null
  /** Số lượt AI mỗi câu trả lời chuẩn (đơn vị khách thấy) — chỉ AI của eCDP. Server cũ: undefined. */
  turnsPerReply?: number | null
}

export interface AiModelsResponse {
  plan: { id: string; name: string; paid: boolean } | null
  models: AiModelEntry[]
}

export interface AiCredits {
  balance: number
  grantBalance: number
  paidBalance: number
  grantPeriodStart?: string | null
  grantPeriodEnd: string | null
  monthlyGrant: number
  /** Đã dùng từ đầu kỳ tặng (grantPeriodStart). */
  usedThisMonth: number
  /** Hạn mức credit / ngày tổ chức TỰ đặt; null = không đặt. */
  dailyCap: number | null
  /** Trần credit / ngày theo gói (Platform đặt) — khách chỉ xem; null = không trần. */
  planDailyCap?: number | null
  /** Trần thực tế = min(gói, tổ chức). */
  effectiveDailyCap?: number | null
  platformEnabled?: boolean
  plan?: { id: string; name: string; paid: boolean } | null
  usedToday: number
  status: WalletStatus
  embedding: { usedToday: number; freeCap: number; overage: EmbeddingOverage } | null
  /** Đơn vị tính nội bộ (đ / đơn vị) — KHÔNG hiện cho khách; chỉ để quy số đơn vị ra đ. */
  creditValueVnd: number
  /** Giá trị 1 "lượt AI" (đ giá trị ví) — đơn vị duy nhất khách thấy. Server cũ: undefined → 50. */
  turnValueVnd?: number
  /** Giá trị đ (giá bán ra) của các số dư / mức dùng. Server cũ: undefined. */
  valueVnd?: AmountMap
  /** ≈ token theo model của tổ chức; null = model tổ chức chạy Key của tôi / chưa có giá → hiện theo đ. */
  tokens?: AmountMap | null
  /** Model tổ chức đang dùng (dùng để quy token). */
  orgModel?: OrgModelRef | null
  /** Giá bán ra / 1 triệu token của model tổ chức; null = không quy được. */
  salePrice?: SalePrice | null
}

/** Các số của ví theo một đơn vị (đ hoặc ≈ token). */
export interface AmountMap {
  total: number
  grant: number
  paid: number
  monthlyGrant: number
  usedThisMonth: number
  usedToday: number
  dailyCap: number | null
  planDailyCap: number | null
  effectiveDailyCap: number | null
}

export interface OrgModelRef { provider: string; model: string; title: string; source: AiSource }

/** Giá bán ra (đ / 1 triệu token): vào, ra, bình quân theo lượt chuẩn 3.000 vào : 400 ra. */
export interface SalePrice { inVnd: number; outVnd: number; blendedVnd: number }

export type LedgerKind = 'grant' | 'grant_expire' | 'purchase' | 'consume' | 'refund' | 'adjust'

export interface LedgerItem {
  id: string
  kind: LedgerKind | string
  bucket: string | null
  amount: number
  balanceAfter: number
  note: string | null
  createdAt: string
  /** consume: tác vụ + ngày + số lượt đã gộp. */
  feature?: string | null
  day?: string | null
  calls?: number | null
  /** Số tiền theo đ (giá bán ra). Server cũ: undefined. */
  amountVnd?: number
  balanceAfterVnd?: number
  /** Dòng Dùng AI: token THẬT đã dùng trong ngày của tác vụ (không quy đổi ngược). */
  usage?: { tokensIn: number; tokensOut: number } | null
}

export interface CreditPack {
  id: string
  /** Giá trị nạp — đơn vị tính nội bộ (không hiện cho khách). */
  credits: number
  /** Giá gói (đ, chưa VAT). */
  price: number
  label: string | null
  /** Giá trị nạp vào ví (đ, giá bán ra) — > giá khi gói có tặng thêm. Server cũ: undefined. */
  valueVnd?: number
  /** ≈ token theo model tổ chức của người mua; null = không quy được. */
  tokensApprox?: number | null
}

export interface BotEffective {
  provider: string | null
  model: string | null
  source: AiSource | null
  creditsPerTurn: number | null
  ready: boolean
  reason: string | null
  /** true = nhân viên AI không đặt model riêng → chạy model của tổ chức (Nguồn AI và lượt AI). Server cũ: undefined. */
  inherited?: boolean
}

// ── Định dạng ────────────────────────────────────────────────────────

/** Số credit kiểu VN: số lẻ chỉ hiện khi nhỏ (1,7 · 12,5 · 42.381). */
export function formatCredits(n: number | null | undefined): string {
  const v = Number(n) || 0
  const abs = Math.abs(v)
  const digits = abs < 10 ? 2 : abs < 1000 ? 1 : 0
  return new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: digits }).format(v)
}

/** "+1.000" / "−250" cho sổ cái. */
export function formatSignedCredits(n: number): string {
  if (n > 0) return `+${formatCredits(n)}`
  if (n < 0) return `−${formatCredits(-n)}`
  return '0'
}

/** Số tiền VND gọn (không ký hiệu): 100.000đ. */
export function formatDong(n: number | null | undefined): string {
  return `${new Intl.NumberFormat(currentLocale()).format(Math.round(Number(n) || 0))}${i18n.t('aiTrainLib:aiCredits.dongSuffix')}`
}

/**
 * Số lớn kiểu tiếng Việt: 950 · 36,4 nghìn · 12,5 triệu · 1,25 tỷ (≥ 10 đơn vị: 1 số lẻ, ≥ 100: không lẻ).
 * Giữ dấu âm.
 */
export function formatBigNumber(n: number | null | undefined): string {
  const v = Number(n) || 0
  const abs = Math.abs(v)
  const units: Array<[number, string]> = [[1e9, i18n.t('aiTrainLib:aiCredits.unit.billion')], [1e6, i18n.t('aiTrainLib:aiCredits.unit.million')], [1e3, i18n.t('aiTrainLib:aiCredits.unit.thousand')]]
  for (const [size, name] of units) {
    if (abs >= size) {
      const x = abs / size
      const digits = x < 10 ? 2 : x < 100 ? 1 : 0
      // Cắt (không làm tròn lên) để "≈ N token" không hứa nhiều hơn số dư.
      const f = Math.floor(x * 10 ** digits) / 10 ** digits
      return `${v < 0 ? '−' : ''}${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: digits }).format(f)} ${name}`
    }
  }
  return `${v < 0 ? '−' : ''}${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(Math.floor(abs))}`
}

/** "12,5 triệu token". */
export function formatTokens(n: number | null | undefined): string {
  return i18n.t('aiTrainLib:aiCredits.tokens', { value: formatBigNumber(n) })
}

/** Giá / 1 triệu token: số đ gọn (giá rất nhỏ giữ 2 số lẻ): 19.500đ · 2.600đ · 0,52đ. */
export function formatPricePer1M(vnd: number | null | undefined): string {
  const v = Number(vnd) || 0
  return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: v < 100 ? 2 : 0 }).format(v)}${i18n.t('aiTrainLib:aiCredits.dongSuffix')}`
}

/** "19.500đ / 1 triệu token vào · 117.000đ / 1 triệu token ra" (giá bán ra). */
export function salePriceLine(sp: { inVnd: number; outVnd: number } | null | undefined): string {
  if (!sp) return ''
  return i18n.t('aiTrainLib:aiCredits.salePriceLine', { inPrice: formatPricePer1M(sp.inVnd), outPrice: formatPricePer1M(sp.outVnd) })
}

/** Bản ngắn cho thẻ / dòng chọn: "19.500đ vào · 117.000đ ra / 1 triệu token". */
export function salePriceShort(sp: { inVnd: number; outVnd: number } | null | undefined): string {
  if (!sp) return ''
  return i18n.t('aiTrainLib:aiCredits.salePriceShort', { inPrice: formatPricePer1M(sp.inVnd), outPrice: formatPricePer1M(sp.outVnd) })
}

// ── Quy đổi hiển thị: giá trị ví → "lượt AI" ─────────────────────────
//
// Ví lưu giá trị (đơn vị tính nội bộ × creditValueVnd = đ theo giá bán ra). KHÁCH CHỈ THẤY "lượt AI" (12/10/2026):
// 1 lượt = turnValueVnd đ giá trị ví (Platform đặt, mặc định 10đ ≈ 1 câu trả lời Gemini 3.1 Flash-Lite). Không hiện "≈ token" / số đ.
// Phần quy token / đ bên dưới chỉ còn cho trang quản trị Platform.

/** Giá trị 1 lượt AI mặc định khi server cũ chưa gửi `turnValueVnd`. */
export const DEFAULT_TURN_VALUE_VND = 10

/** Bộ quy đổi: đơn vị nội bộ → đ → lượt AI (và ≈ token cho Platform). */
export interface TokenRate {
  creditValueVnd: number
  /** đ giá trị ví / 1 lượt AI. */
  turnValueVnd: number
  /** đ / 1 triệu token (bình quân); null = không quy được → hiện đ. */
  blendedVnd: number | null
  modelTitle: string | null
}

/** Bộ quy đổi từ ví (GET /ai/credits). Server cũ (thiếu salePrice) → chỉ đ. */
export function tokenRateOf(c: Pick<AiCredits, 'creditValueVnd' | 'salePrice' | 'orgModel' | 'turnValueVnd'> | null | undefined): TokenRate | null {
  if (!c) return null
  const blended = c.salePrice?.blendedVnd
  return {
    creditValueVnd: Number(c.creditValueVnd) || 0,
    turnValueVnd: Number(c.turnValueVnd) > 0 ? Number(c.turnValueVnd) : DEFAULT_TURN_VALUE_VND,
    blendedVnd: blended && blended > 0 ? blended : null,
    modelTitle: blended && blended > 0 ? c.orgModel?.title ?? null : null,
  }
}

/** Giá trị đ → ≈ token (giữ dấu, làm tròn về 0). Không quy được → null. */
export function vndToTokens(vnd: number | null | undefined, blendedVnd: number | null | undefined): number | null {
  if (vnd == null || !blendedVnd || blendedVnd <= 0) return null
  const t = Math.floor(Math.round((Math.abs(vnd) / blendedVnd) * 1e6 * 1e3) / 1e3)
  return vnd < 0 ? -t : t
}

/** Đơn vị nội bộ → đ. */
export function unitsToVnd(units: number | null | undefined, rate: Pick<TokenRate, 'creditValueVnd'> | null | undefined): number {
  return Math.round((Number(units) || 0) * (rate?.creditValueVnd ?? 0) * 100) / 100
}

/** đ giá trị ví → số lượt AI (giữ dấu, cắt về 1 số lẻ — không hứa nhiều hơn số dư). */
export function vndToTurns(vnd: number | null | undefined, rate: Pick<TokenRate, 'turnValueVnd'> | null | undefined): number {
  const per = rate?.turnValueVnd && rate.turnValueVnd > 0 ? rate.turnValueVnd : DEFAULT_TURN_VALUE_VND
  const t = Math.abs(Number(vnd) || 0) / per
  const cut = Math.floor(Math.round(t * 1e6) / 1e5) / 10
  return (Number(vnd) || 0) < 0 ? -cut : cut
}

/** Đơn vị nội bộ → số lượt AI. */
export function unitsToTurns(units: number | null | undefined, rate: Pick<TokenRate, 'creditValueVnd' | 'turnValueVnd'> | null | undefined): number {
  return vndToTurns(unitsToVnd(units, rate), rate)
}

/** "70.000 lượt AI" · "2,5 lượt AI" (số lẻ chỉ khi < 100). */
export function formatTurns(n: number | null | undefined, suffix = i18n.t('aiTrainLib:aiCredits.turnsSuffix')): string {
  const v = Number(n) || 0
  const digits = Math.abs(v) < 100 && !Number.isInteger(v) ? 1 : 0
  const f = digits ? v : Math.trunc(v)
  return `${new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: digits }).format(f)}${suffix}`
}

/** "+525" / "−1,5" lượt cho sổ cái. */
export function formatSignedTurns(n: number, suffix = ''): string {
  if (n > 0) return `+${formatTurns(n, suffix)}`
  if (n < 0) return `−${formatTurns(-n, suffix)}`
  return formatTurns(0, suffix)
}

/** Một lượng đơn vị nội bộ → "70.000 lượt AI" (đơn vị duy nhất khách thấy). */
export function amountText(units: number | null | undefined, rate: TokenRate | null | undefined): string {
  return formatTurns(unitsToTurns(units, rate))
}

/** Số dư ví cho khách: "30.400 lượt AI" (không kèm ≈ token / đ). `vnd` giữ cho chỗ cần tính. */
export function walletHeadline(c: AiCredits): { main: string; sub: string | null; turns: number; vnd: number } {
  const rate = tokenRateOf(c)
  // Tính từ `balance` (socket ai:credits chỉ đẩy balance mới) — cùng công thức server; thiếu đơn vị → số server gửi.
  const vnd = rate?.creditValueVnd ? unitsToVnd(c.balance, rate) : c.valueVnd?.total ?? 0
  const turns = vndToTurns(vnd, rate)
  return { main: formatTurns(turns), sub: null, turns, vnd }
}

/** Số lượt AI mỗi câu trả lời của model (lượt chuẩn 3.000 token vào : 400 ra) — từ creditsPerTurn (đơn vị nội bộ). */
export function turnsPerReply(m: Pick<AiModelEntry, 'creditsPerTurn' | 'turnsPerReply'> | undefined, rate?: Pick<TokenRate, 'creditValueVnd' | 'turnValueVnd'> | null): number | null {
  if (m?.turnsPerReply != null) return m.turnsPerReply
  if (!m?.creditsPerTurn || !rate?.creditValueVnd) return null
  const per = rate.turnValueVnd > 0 ? rate.turnValueVnd : DEFAULT_TURN_VALUE_VND
  return Math.max(0.1, Math.round(((m.creditsPerTurn * rate.creditValueVnd) / per) * 10) / 10)
}

/** "≈ 1 lượt / câu trả lời" · "≈ 7,8 lượt / câu trả lời". */
export function perReplyText(m: Pick<AiModelEntry, 'creditsPerTurn' | 'turnsPerReply'> | undefined, rate?: Pick<TokenRate, 'creditValueVnd' | 'turnValueVnd'> | null): string | null {
  const t = turnsPerReply(m, rate)
  return t == null ? null : i18n.t('aiTrainLib:aiCredits.perReply', { turns: formatTurns(t, i18n.t('aiTrainLib:aiCredits.turnsShortSuffix')) })
}

/** Giá trị đ của gói (server cũ: đơn vị × creditValueVnd). */
export function packValueVnd(p: Pick<CreditPack, 'credits' | 'valueVnd'>, creditValueVnd?: number | null): number {
  return p.valueVnd ?? Math.round(p.credits * (creditValueVnd ?? 0))
}

/** Số lượt AI của gói (theo GIÁ TRỊ nạp, đã gồm tặng thêm). */
export function packTurns(p: Pick<CreditPack, 'credits' | 'valueVnd'>, rate: Pick<TokenRate, 'creditValueVnd' | 'turnValueVnd'> | null | undefined): number {
  return vndToTurns(packValueVnd(p, rate?.creditValueVnd), rate)
}

/** Nhãn gói: "10.500 lượt AI — 500.000đ". */
export function packHeadline(
  p: Pick<CreditPack, 'price' | 'credits' | 'valueVnd'>,
  rate: TokenRate | null | undefined,
): string {
  return i18n.t('aiTrainLib:aiCredits.packHeadline', { turns: formatTurns(packTurns(p, rate)), price: formatDong(p.price) })
}

/** % tặng thêm của gói = giá trị nạp / giá − 1 (0 khi không tặng). */
export function packBonusPercent(p: Pick<CreditPack, 'price' | 'credits' | 'valueVnd'>, creditValueVnd?: number | null): number {
  const v = packValueVnd(p, creditValueVnd)
  if (!p.price || !v) return 0
  return Math.max(0, Math.round((v / p.price - 1) * 100))
}

// ── Trạng thái ví ────────────────────────────────────────────────────

export const LOW_BALANCE_FLOOR = 1000

/**
 * Trạng thái ví: ưu tiên `status` server trả; server cũ / thiếu → tự suy ra
 * (hết = ≤ 0; sắp hết = ≤ 20% credit tặng / tháng của gói; gói không tặng → ≤ 1.000).
 */
export function deriveWalletStatus(c: Pick<AiCredits, 'balance' | 'monthlyGrant'> & { status?: WalletStatus | null }): WalletStatus {
  if (c.status === 'ok' || c.status === 'low' || c.status === 'empty' || c.status === 'blocked') return c.status
  if (c.balance <= 0) return 'empty'
  const threshold = c.monthlyGrant > 0 ? c.monthlyGrant * 0.2 : LOW_BALANCE_FLOOR
  return c.balance <= threshold ? 'low' : 'ok'
}

export type BadgeTone = 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'

export const WALLET_STATUS_META: Record<WalletStatus, { label: string; tone: BadgeTone }> = {
  ok: { get label() { return i18n.t('aiTrainLib:aiCredits.walletStatus.ok') }, tone: 'success' },
  low: { get label() { return i18n.t('aiTrainLib:aiCredits.walletStatus.low') }, tone: 'warning' },
  empty: { get label() { return i18n.t('aiTrainLib:aiCredits.walletStatus.empty') }, tone: 'destructive' },
  blocked: { get label() { return i18n.t('aiTrainLib:aiCredits.walletStatus.blocked') }, tone: 'destructive' },
}

/** AI của eCDP có đang chạy được không (ví còn, không bị khoá). */
export function platformUsable(status: WalletStatus | null | undefined): boolean {
  return status !== 'empty' && status !== 'blocked'
}

// ── Nguồn theo nhà cung cấp ──────────────────────────────────────────

export const SOURCE_LABEL: Record<AiSource, string> = {
  get platform() { return i18n.t('aiTrainLib:aiCredits.source.platform') },
  get byok() { return i18n.t('aiTrainLib:aiCredits.source.byok') },
}

/** Key BYOK đang lỗi (đã lưu nhưng xác thực thất bại). */
export function byokBroken(b: ByokState | null | undefined): boolean {
  if (!b?.configured) return false
  return !!b.lastError || (!!b.status && b.status !== 'active')
}

/** Badge trên ô nhà cung cấp. */
export function providerSourceBadge(
  p: Pick<AiProviderSource, 'source' | 'byok' | 'platformAvailable'>,
  ctx: { walletStatus?: WalletStatus | null; platformEnabled?: boolean; orgBlocked?: boolean } = {},
): { label: string; tone: BadgeTone } {
  if (p.source === 'platform') {
    if (ctx.platformEnabled === false || ctx.orgBlocked || !p.platformAvailable) return { label: i18n.t('aiTrainLib:aiCredits.badge.ecdpPaused'), tone: 'destructive' }
    if (ctx.walletStatus === 'empty' || ctx.walletStatus === 'blocked') return { label: i18n.t('aiTrainLib:aiCredits.walletStatus.empty'), tone: 'destructive' }
    return { label: i18n.t('aiTrainLib:aiCredits.source.platform'), tone: 'default' }
  }
  if (!p.byok?.configured) return { label: i18n.t('aiTrainLib:aiCredits.badge.needKey'), tone: 'warning' }
  if (byokBroken(p.byok)) return { label: i18n.t('aiTrainLib:aiCredits.badge.keyBroken'), tone: 'destructive' }
  return { label: i18n.t('aiTrainLib:aiCredits.badge.okOwnKey'), tone: 'success' }
}

// ── Model: lọc / khoá / nhãn ─────────────────────────────────────────

export function modelsOfProvider(models: AiModelEntry[], provider: string): AiModelEntry[] {
  return models.filter((m) => m.provider === provider)
}

export function findModel(models: AiModelEntry[], provider: string | null | undefined, model: string | null | undefined): AiModelEntry | undefined {
  if (!provider || !model) return undefined
  return models.find((m) => m.provider === provider && m.model === model)
}

/** Lý do khoá hiển thị (🔒 …) — null nếu dùng được. */
export function lockText(m: Pick<AiModelEntry, 'allowed' | 'lockReason'>): string | null {
  if (m.allowed) return null
  return m.lockReason || i18n.t('aiTrainLib:aiCredits.lock.unavailable')
}

/** Nhãn một dòng trong ô chọn model: "GPT-5.4 Mini · 19.500đ vào · 117.000đ ra / 1 triệu token" / "· 🔒 …" / "· key riêng". */
export function modelOptionLabel(m: AiModelEntry): string {
  const lock = lockText(m)
  if (lock) return i18n.t('aiTrainLib:aiCredits.modelOption.locked', { title: m.title, lock })
  if (m.source === 'byok') return i18n.t('aiTrainLib:aiCredits.modelOption.ownKey', { title: m.title })
  return m.salePricePer1M ? i18n.t('aiTrainLib:aiCredits.modelOption.priced', { title: m.title, price: salePriceShort(m.salePricePer1M) }) : m.title
}

/** Model đầu tiên dùng được của nhà cung cấp (khi đổi nhà cung cấp). */
export function firstAllowedModel(models: AiModelEntry[], provider: string): AiModelEntry | undefined {
  return modelsOfProvider(models, provider).find((m) => m.allowed)
}

/** Số model dùng được / tổng của một nhà cung cấp. */
export function allowedCount(models: AiModelEntry[], provider: string): { allowed: number; total: number } {
  const list = modelsOfProvider(models, provider)
  return { allowed: list.filter((m) => m.allowed).length, total: list.length }
}

/** Dòng chi phí dưới ô chọn model: giá bán ra / 1 triệu token + ví đủ ≈ N token của model đó. */
export function costLine(m: AiModelEntry | undefined, providerName?: string, rate?: TokenRate | null): string {
  if (!m) return ''
  if (m.source === 'byok') {
    return i18n.t('aiTrainLib:aiCredits.costLine.byok', { provider: providerName ?? m.provider })
  }
  const lock = lockText(m)
  if (lock) return i18n.t('aiTrainLib:aiCredits.costLine.locked', { lock })
  const per = perReplyText(m, rate)
  return per ? i18n.t('aiTrainLib:aiCredits.costLine.platform', { per }) : i18n.t('aiTrainLib:aiCredits.costLine.platformDefault')
}

/** Số lượt ước tính với số dư hiện tại. */
export function estimateTurns(balance: number, creditsPerTurn: number | null | undefined): number | null {
  if (!creditsPerTurn || creditsPerTurn <= 0) return null
  return Math.max(0, Math.floor(balance / creditsPerTurn))
}

// ── Nhân viên AI: nguồn thật (effective) ─────────────────────────────

export function botSourceLine(e: BotEffective | null | undefined): { label: string; tone: BadgeTone } | null {
  if (!e?.source) return null
  if (e.source === 'byok') return { label: i18n.t('aiTrainLib:aiCredits.source.byok'), tone: 'success' }
  return { label: i18n.t('aiTrainLib:aiCredits.source.platform'), tone: 'default' }
}

// ── Mua credit: tạm tính + VAT ───────────────────────────────────────

export interface PackTotals { subtotal: number; vatPercent: number; vat: number; total: number }

/** Tạm tính phía client (hiển thị ngay; số thật lấy từ /billing/ai-credits/quote). */
export function packTotals(pack: Pick<CreditPack, 'price'>, vatPercent: number | null | undefined): PackTotals {
  const pct = typeof vatPercent === 'number' && Number.isFinite(vatPercent) ? Math.min(100, Math.max(0, vatPercent)) : 10
  const subtotal = Math.max(0, Math.round(pack.price))
  const vat = Math.round((subtotal * pct) / 100)
  return { subtotal, vatPercent: pct, vat, total: subtotal + vat }
}

/** Đơn giá (đ trả / đơn vị nạp) — so sánh gói. */
export function unitPrice(pack: Pick<CreditPack, 'price' | 'credits'>): number {
  return pack.credits > 0 ? pack.price / pack.credits : 0
}

/** % tiết kiệm so với gói đắt nhất / credit. */
export function packSavingPercent(pack: CreditPack, packs: CreditPack[]): number {
  const base = Math.max(...packs.map(unitPrice), 0)
  if (!base) return 0
  return Math.max(0, Math.round((1 - unitPrice(pack) / base) * 100))
}

// ── Nhúng tài liệu ───────────────────────────────────────────────────

export function embeddingProgress(e: { usedToday: number; freeCap: number }): { percent: number; capped: boolean } {
  if (!e.freeCap || e.freeCap <= 0) return { percent: 100, capped: true }
  const percent = Math.min(100, Math.round((e.usedToday / e.freeCap) * 100))
  return { percent, capped: e.usedToday >= e.freeCap }
}

// ── Sổ cái & tác vụ ──────────────────────────────────────────────────

export const LEDGER_KIND_LABEL: Record<LedgerKind, string> = {
  get grant() { return i18n.t('aiTrainLib:aiCredits.ledger.grant') },
  get grant_expire() { return i18n.t('aiTrainLib:aiCredits.ledger.grant_expire') },
  get purchase() { return i18n.t('aiTrainLib:aiCredits.ledger.purchase') },
  get consume() { return i18n.t('aiTrainLib:aiCredits.ledger.consume') },
  get refund() { return i18n.t('aiTrainLib:aiCredits.ledger.refund') },
  get adjust() { return i18n.t('aiTrainLib:aiCredits.ledger.adjust') },
}

/** Số tiền dòng sổ cái theo đ, giá bán ra (server cũ: đơn vị × creditValueVnd). */
export function ledgerVnd(r: Pick<LedgerItem, 'amount' | 'balanceAfter' | 'amountVnd' | 'balanceAfterVnd'>, rate: Pick<TokenRate, 'creditValueVnd'> | null | undefined): { amount: number; after: number } {
  return { amount: r.amountVnd ?? unitsToVnd(r.amount, rate), after: r.balanceAfterVnd ?? unitsToVnd(r.balanceAfter, rate) }
}

/** Số lượt dòng sổ cái (từ đ giá bán ra). */
export function ledgerTurns(r: Pick<LedgerItem, 'amount' | 'balanceAfter' | 'amountVnd' | 'balanceAfterVnd'>, rate: Pick<TokenRate, 'creditValueVnd' | 'turnValueVnd'> | null | undefined): { amount: number; after: number } {
  const v = ledgerVnd(r, rate)
  return { amount: vndToTurns(v.amount, rate), after: vndToTurns(v.after, rate) }
}

/** "+525.000đ" / "−150đ". */
export function formatSignedDong(n: number): string {
  if (n > 0) return `+${formatDong(n)}`
  if (n < 0) return `−${formatDong(-n)}`
  return formatDong(0)
}

export function ledgerKindLabel(kind: string): string {
  return (LEDGER_KIND_LABEL as Record<string, string>)[kind] ?? kind
}

export const FEATURE_LABEL: Record<string, string> = {
  get auto_reply() { return i18n.t('aiTrainLib:aiCredits.feature.auto_reply') },
  get ai_router() { return i18n.t('aiTrainLib:aiCredits.feature.ai_router') },
  get reply_draft() { return i18n.t('aiTrainLib:aiCredits.feature.reply_draft') },
  get suggest() { return i18n.t('aiTrainLib:aiCredits.feature.suggest') },
  get summary() { return i18n.t('aiTrainLib:aiCredits.feature.summary') },
  get sentiment() { return i18n.t('aiTrainLib:aiCredits.feature.sentiment') },
  get lead_score() { return i18n.t('aiTrainLib:aiCredits.feature.lead_score') },
  get ai_cdp() { return i18n.t('aiTrainLib:aiCredits.feature.ai_cdp') },
  get ai_master() { return i18n.t('aiTrainLib:aiCredits.feature.ai_master') },
  get embedding() { return i18n.t('aiTrainLib:aiCredits.feature.embedding') },
  get critic() { return i18n.t('aiTrainLib:aiCredits.feature.critic') },
  get assistant() { return i18n.t('aiTrainLib:aiCredits.feature.assistant') },
  get eval() { return i18n.t('aiTrainLib:aiCredits.feature.eval') },
}

export function featureLabel(f: string): string {
  return FEATURE_LABEL[f] ?? f
}

// ── Lỗi theo mã ──────────────────────────────────────────────────────

export const AI_ERROR_MESSAGES: Record<string, string> = {
  get AI_NO_KEY() { return i18n.t('aiTrainLib:aiCredits.error.AI_NO_KEY') },
  get AI_KEY_INVALID() { return i18n.t('aiTrainLib:aiCredits.error.AI_KEY_INVALID') },
  get AI_NO_CREDIT() { return i18n.t('aiTrainLib:aiCredits.error.AI_NO_CREDIT') },
  get AI_MODEL_NOT_ALLOWED() { return i18n.t('aiTrainLib:aiCredits.error.AI_MODEL_NOT_ALLOWED') },
  get AI_PLATFORM_DISABLED() { return i18n.t('aiTrainLib:aiCredits.error.AI_PLATFORM_DISABLED') },
  get AI_DAILY_CAP() { return i18n.t('aiTrainLib:aiCredits.error.AI_DAILY_CAP') },
  get AI_DISABLED() { return i18n.t('aiTrainLib:aiCredits.error.AI_DISABLED') },
  get AI_EMBED_CAP() { return i18n.t('aiTrainLib:aiCredits.error.AI_EMBED_CAP') },
}

type ErrBody = { error?: string | { code?: string; message?: string }; code?: string; message?: string }

/** Lấy mã lỗi từ lỗi axios — chuẩn repo `{ error: "<thông báo>", code }`; nhận cả dạng cũ `{ error: { code, message } }`. */
export function aiErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: ErrBody } })?.response?.data
  if (!data) return null
  if (data.error && typeof data.error === 'object' && data.error.code) return data.error.code
  return data.code ?? null
}

/** Câu tiếng Việt dễ hiểu cho lỗi AI; mã lạ → message server → câu chung. */
export function aiErrorMessage(err: unknown, fallback = i18n.t('aiTrainLib:aiCredits.error.generic')): string {
  const code = aiErrorCode(err)
  if (code && AI_ERROR_MESSAGES[code]) return AI_ERROR_MESSAGES[code]
  const data = (err as { response?: { data?: ErrBody } })?.response?.data
  if (data) {
    if (data.error && typeof data.error === 'object' && data.error.message) return data.error.message
    const text = apiErrorText(data)
    if (text) return text
  }
  const msg = (err as { message?: string })?.message
  return msg || fallback
}

export function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/** API chưa có (BE đang làm song song) → 404/405/501: ẩn phần mới thay vì báo lỗi. */
export function isApiMissing(err: unknown): boolean {
  const s = httpStatus(err)
  return s === 404 || s === 405 || s === 501
}

// ── Gói dịch vụ: credit AI / tháng ───────────────────────────────────

/**
 * Dòng quyền lợi AI của gói ở /billing: "Token AI tặng / tháng" (≈ token theo model tổ chức, không quy được → đ) nếu bảng
 * giá có `aiCreditsPerMonth`, không thì fallback trường cũ "lượt AI / ngày".
 */
/** Nhãn dòng AI tặng của gói (nơi gọi so nhãn này để chọn cách hiển thị). */
// Hằng nhận diện (nơi gọi so sánh bằng ===), không hiển thị trực tiếp → giữ nguyên.
export const PLAN_AI_LABEL = 'lượt AI tặng / tháng' // i18n-ignore

export function planAiLimit(
  plan: { aiCreditsPerMonth?: number | null; aiRepliesPerDay?: number | null },
  rate?: TokenRate | null,
): { label: string; value: number | null; text: string | null } {
  if (plan.aiCreditsPerMonth !== undefined) {
    const units = plan.aiCreditsPerMonth ?? null
    // Chưa có bộ quy đổi (chưa tải ví) → text null: nơi gọi tự ẩn số thay vì hiện đơn vị nội bộ.
    const text = units === 0 ? i18n.t('aiTrainLib:aiCredits.plan.noGrant') : units && rate?.creditValueVnd ? amountText(units, rate) : null
    return { label: PLAN_AI_LABEL, value: units, text }
  }
  const v = plan.aiRepliesPerDay ?? null
  return { label: i18n.t('aiTrainLib:aiCredits.plan.perDay'), value: v, text: v == null ? null : v.toLocaleString(currentLocale()) }
}

// ── Form nhân viên AI: dòng nguồn + chi phí ──────────────────────────

export interface SourceInfo { tone: 'info' | 'ok' | 'warn' | 'danger'; text: string; buy?: boolean }

/**
 * Dòng dưới ô Model AI của form nhân viên AI: nguồn, credit/lượt, cảnh báo thiếu key / hết credit.
 * `model` = model thật sẽ chạy (của bot hoặc mặc định của tổ chức).
 */
export function formSourceInfo(opts: {
  model: AiModelEntry | undefined
  byok?: ByokState | null
  credits?: Pick<AiCredits, 'balance' | 'monthlyGrant' | 'status'> | null
  providerName: string
  rate?: TokenRate | null
}): SourceInfo | null {
  const { model: m, byok, credits, providerName, rate } = opts
  if (!m) return null
  if (m.source === 'byok') {
    if (byok && !byok.configured) {
      return { tone: 'warn', text: i18n.t('aiTrainLib:aiCredits.form.noKey', { provider: providerName }) }
    }
    if (byokBroken(byok)) return { tone: 'danger', text: i18n.t('aiTrainLib:aiCredits.form.keyBroken', { provider: providerName }) }
    return { tone: 'ok', text: i18n.t('aiTrainLib:aiCredits.form.byokOk', { provider: providerName }) }
  }
  const lock = lockText(m)
  if (lock) return { tone: 'warn', text: i18n.t('aiTrainLib:aiCredits.form.modelLocked', { lock }) }
  const status = credits ? deriveWalletStatus(credits) : null
  if (status === 'empty') return { tone: 'danger', text: i18n.t('aiTrainLib:aiCredits.form.empty'), buy: true }
  if (status === 'blocked') return { tone: 'danger', text: i18n.t('aiTrainLib:aiCredits.form.blocked') }
  const per = perReplyText(m, rate) ?? i18n.t('aiTrainLib:aiCredits.form.perReplyDefault')
  const left = credits && rate ? i18n.t('aiTrainLib:aiCredits.form.left', { amount: amountText(credits.balance, rate) }) : ''
  return { tone: status === 'low' ? 'warn' : 'info', text: i18n.t('aiTrainLib:aiCredits.form.platform', { per, left }), buy: status === 'low' }
}
